import { agent } from '@shared/services/agent';
import moment, { Moment } from "moment";
import { CompanyHolidayUsageSummary, HolidayBooking } from '@features/holidays/models/Holiday.model';
import {
  HolidayGroupBookingRequest,
  HolidayGrouping,
  UserHolidayInfoResponse,
  UsersHolidayInfo
} from "../pages/models/Holidays.model";
import { DATE_FORMAT } from "@shared/utils/DateUtils";
import { BasicUserInfo, UserListItem } from "@shared/models/user.models";
import { CompanyHoliday } from "./CompanyHolidayService";
import { naturalComparator } from "@shared/utils/ArrayUtils";
import { SideOfDay } from '@features/whereabouts/models/movements.models';
import { ApprovalState } from '@features/holidays/models/approval-requests.models';


export const LINE_REPORT_ID = 'lineReports';
export const INDIRECT_LINE_REPORT_ID = 'indirectLineReports';

export async function requestUsersHolidayOverview(): Promise<UserHolidayOverviewResponse> {
  const holidayOverview = await agent.requests.get('/api/holidays');
  return {
    ...holidayOverview,
    holidays: holidayOverview.holidays.map((booking: HolidayBookingEntity) => new HolidayBooking(booking))
  }
}

export async function postNewHoliday(name: string, emailNotification: boolean, from: Moment, to: Moment): Promise<HolidayBooking> {
  const booking: HolidayBookingEntity = await agent.requests.post('/api/holidays', {
    from: from.format('YYYY-MM-DD'),
    to: to.format('YYYY-MM-DD'),
    name: name,
    emailNotification: emailNotification,
  }, true);
  return new HolidayBooking(booking);
}

export async function updateNewHoliday(id: number, name: string, emailNotification: boolean, from: Moment, to: Moment): Promise<HolidayBooking> {
  const booking: HolidayBookingEntity = await agent.requests.put('/api/holidays', {
    id: id,
    from: from.format('YYYY-MM-DD'),
    to: to.format('YYYY-MM-DD'),
    name: name,
    emailNotification: emailNotification,
  }, true);
  return new HolidayBooking(booking);
}

export async function getHolidayBooking(id: number): Promise<HolidayBooking> {
  const booking = await agent.requests.get(`/api/holiday/${id}`);
  return new HolidayBooking(booking);
}

export async function deleteHolidayBookingRequest(id: number) {
  return await agent.requests.del(`/api/holiday/${id}`);
}

export async function fetchAllUsersHolidaysInCompany(year: number): Promise<CompanyHolidayUsageSummary> {
  return await agent.requests.get(`/api/v2/holidays/company-usage?year=${year}`);
}

export async function fetchAllUsersHolidaysInTeam(year: number, teamId: string): Promise<CompanyHolidayUsageSummary> {
  return await agent.requests.get(`/api/v2/holidays/team-usage?year=${year}&teamId=${teamId}`);
}

export async function fetchUsersHolidayInfoV2(dateString?: string, userId?: string): Promise<UserHolidayInfoResponse> {
  let params = `date=${dateString ?? moment().format(DATE_FORMAT)}`
  if (userId) {
    params += `&userId=${userId}`;
  }
  return await agent.requests.get(`/api/v2/holidays/user?${params}`);
}

export async function bookHolidayV2(groupBookingRequest: HolidayGroupBookingRequest): Promise<void> {
  return await agent.requests.post(`/api/v2/holidays/book`, groupBookingRequest);
}

export async function deleteHolidayV2(groupBookingRequest: HolidayGroupBookingRequest): Promise<void> {
  return await agent.requests.post(`/api/v2/holidays/delete`, groupBookingRequest);
}

export async function getTeamHolidays(teamIds: string[], workGroupIds: number[], date: Moment, departmentIds: number[] = []): Promise<MonthlyHolidayView[]> {
  const result = await agent.requests.post(`/api/v2/holidays/team`, {
    teamIds: teamIds,
    workGroupIds: workGroupIds,
    departmentIds: departmentIds,
    date: date.format(DATE_FORMAT)
  });

  return result.sort((a: MonthlyHolidayView, b: MonthlyHolidayView) => naturalComparator(a.user, b.user, 'firstName'))
}

export async function getUsersHolidaysCanBeAddedTo(): Promise<UserListItem[]> {
  return await agent.requests.get('/api/v2/holidays/users')
}

export async function getUsersHolidayInfoForGroup(group: string, date: Moment): Promise<UsersHolidayInfo[]> {
  return await agent.requests.get(`/api/v2/holidays/user-info?group=${group}&date=${date.format(DATE_FORMAT)}`)
}

export async function getUsersHolidayUsage(userId: string, dateString: string): Promise<UsersHolidayInfo> {
  return await agent.requests.get(`/api/v2/holidays/user-info/${userId}/${dateString}`)
}

export async function updateHolidayAllowance(holidayAllowanceRequest: HolidayAllowanceRequest) {
  return await agent.requests.put(`/api/holiday/allowances`, holidayAllowanceRequest)
}

export async function getEstimatedWorkDaysBooked(request: CalculateHolidaysUsedRequest): Promise<HolidaysUsedCalculationResponse> {
  return await agent.requests.put(`/api/holiday/calculate-days-requesting`, request)
}

export interface HolidayAllowanceRequest {
  holidayYear: number;
  userId: string;
  basicAllowance: number;
  additionalDays: number;
  daysCarriedOver: number;
}

export interface UserHolidayOverviewResponse {
  holidays: HolidayBooking[];
  daysTaken: number;
  daysRemaining: number;
  totalAllowance: number;
}

export interface HolidayBookingEntity {
  id: number;
  name: string;
  emailNotification: boolean;
  fromDate: string;
  toDate: string;
  bookerName: string;
}

export interface MonthlyHolidayView {
  user: BasicUserInfo;
  holidayBookings: HolidayBooking[];
  holidayGroupings: HolidayGrouping[];
  companyHolidays: CompanyHoliday[];
  nonWorkingDays: NonWorkingDays[];
  customLeaveDays: CustomLeaveDays[];
  customNonWorkingDays: CustomNonWorkingDays[];
}

export interface NonWorkingDays {
  date: string;
  am: boolean;
  pm: boolean;
  userId: string;
}

export interface CustomNonWorkingDays {
  date: string;
  am: boolean;
  pm: boolean;
  amWhereaboutsId?: number;
  pmWhereaboutsId?: number;
  amImage?: string;
  pmImage?: string;
  userId: string;
}

export interface CustomLeaveDays {
  date: string;
  am: boolean;
  pm: boolean;
  userId: string;
  amWhereaboutsId: number;
  pmWhereaboutsId: number;
  state?: ApprovalState;
}

export interface SchengenUsage {
  date: string;
  daysUsed: number;
}

interface CalculateHolidaysUsedRequest {
  userId: string;
  dateFrom: string;
  dateTo: string;
  firstDaySideOfDay: SideOfDay;
  lastDaySideOfDay: SideOfDay;
}

interface HolidaysUsedCalculationResponse {
  userId: string;
  dateFrom: string;
  dateTo: string;
  daysUsed: number;
}
